"use client";

import React, { useEffect } from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal } from "@/components/shared/motion";
import { ShieldCheck, ArrowRight } from "lucide-react";

/**
 * SkyLane-native compatibility teaser — a crisp solid white banner nudging
 * travelers to the device checker. Solid electric-blue CTA, no glass/gradient.
 * Market-test: wf_compatibility_counter_v1 (market-test-plan.md §3; research/07
 * CF compatibility-as-trust — device confidence before purchase).
 */
const EXPERIMENT_ID = "wf_compatibility_counter_v1";

export default function CompatTeaser() {
  const { t } = useTranslations();

  useEffect(() => {
    try {
      track({
        event_name: "compatibility_teaser_viewed",
        event_id: `compatibility_teaser_viewed:${EXPERIMENT_ID}`,
        experiment_id: EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
  }, []);

  const onCheckClick = () => {
    try {
      track({
        event_name: "compatibility_checker_started",
        event_id: `compatibility_checker_started:${EXPERIMENT_ID}:${Date.now()}`,
        experiment_id: EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
  };

  return (
    <section className="container mx-auto px-4 py-8">
      <Reveal as="div" y={16} className="rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:shadow-md hover:border-primary/40 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 text-primary shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </span>
          <div>
            <div className="font-semibold text-foreground">
              {t("sky.compatTeaser.title", "Not sure if your device is eSIM compatible?")}
            </div>
            <div className="text-sm text-muted-foreground">
              {t("sky.compatTeaser.body", "Check in 10 seconds and travel with confidence.")}
            </div>
          </div>
        </div>
        <Link
          href="/compatible-phones/"
          onClick={onCheckClick}
          className="group inline-flex items-center gap-2 rounded-xl bg-primary text-primary-foreground font-semibold px-6 h-11 shrink-0 hover:bg-primary/90 transition-all active:scale-[0.98]"
        >
          {t("sky.compatTeaser.cta", "Check compatibility")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </Reveal>
    </section>
  );
}
